import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { firmaAPI } from '../api';
import FirmaModal from '../components/FirmaModal';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Edit2, Mail, Phone, MapPin, Briefcase,
  StickyNote, Clock, Shield, Hash
} from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

const ACTION_LABELS = {
  CREATE: { label: 'Oluşturuldu', color: 'bg-emerald-100 text-emerald-700' },
  UPDATE: { label: 'Güncellendi', color: 'bg-blue-100 text-blue-700' },
  DELETE: { label: 'Silindi', color: 'bg-red-100 text-red-700' },
};

function InfoRow({ icon: Icon, label, value, href }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-navy-50 last:border-0">
      <div className="w-7 h-7 rounded-lg bg-navy-100 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon size={14} className="text-navy-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-navy-400 mb-0.5">{label}</p>
        {href ? (
          <a href={href} className="text-navy-700 font-medium text-sm hover:text-navy-900 truncate block">
            {value}
          </a>
        ) : (
          <p className="text-navy-700 font-medium text-sm break-words">{value || '—'}</p>
        )}
      </div>
    </div>
  );
}

export default function FirmaDetayPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [firma, setFirma] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  const load = () => {
    setLoading(true);
    firmaAPI.get(id)
      .then((r) => setFirma(r.data))
      .catch(() => { toast.error('Firma bulunamadı'); navigate('/firmalar'); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-navy-200 border-t-navy-600 rounded-full animate-spin" />
    </div>
  );

  if (!firma) return null;

  return (
    <div className="max-w-4xl animate-fade-in space-y-5">
      {/* Geri + başlık */}
      <div className="flex items-center gap-3">
        <button className="btn-ghost p-2" onClick={() => navigate('/firmalar')}>
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-display text-navy-800 truncate">{firma.firma_adi}</h1>
          <p className="text-navy-400 text-xs">#{firma.id}</p>
        </div>
        <button className="btn-primary" onClick={() => setEditing(true)}>
          <Edit2 size={15} /> Düzenle
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Sol — bilgiler */}
        <div className="lg:col-span-2 space-y-5">
          {/* Ana bilgiler */}
          <div className="card p-5">
            <h2 className="text-sm font-semibold text-navy-700 uppercase tracking-wider mb-4">
              Firma Bilgileri
            </h2>
            <InfoRow icon={Hash} label="Sıra No" value={firma.sira?.toString()} />
            <InfoRow icon={MapPin} label="İlçe / OSB" value={firma.ilce} />
            <InfoRow icon={Briefcase} label="İlgili Kurum" value={firma.ilgili_kurum} />
            <InfoRow icon={Mail} label="Mail Adresi" value={firma.mail}
              href={firma.mail ? `mailto:${firma.mail}` : undefined} />
            <InfoRow icon={Phone} label="Telefon" value={firma.telefon}
              href={firma.telefon ? `tel:${firma.telefon}` : undefined} />
          </div>

          {/* Notlar */}
          {firma.notlar && (
            <div className="card p-5">
              <div className="flex items-center gap-2 mb-3">
                <StickyNote size={15} className="text-gold-500" />
                <h2 className="text-sm font-semibold text-navy-700">Notlar</h2>
              </div>
              <p className="text-navy-600 text-sm leading-relaxed whitespace-pre-wrap">{firma.notlar}</p>
            </div>
          )}

          {/* Audit geçmişi */}
          <div className="card p-5">
            <div className="flex items-center gap-2 mb-4">
              <Shield size={15} className="text-navy-500" />
              <h2 className="text-sm font-semibold text-navy-700">Değişiklik Geçmişi</h2>
            </div>
            {firma.audit_gecmisi?.length === 0 ? (
              <p className="text-navy-400 text-sm">Henüz kayıt yok</p>
            ) : (
              <div className="space-y-3">
                {firma.audit_gecmisi?.map((log, i) => {
                  const meta = ACTION_LABELS[log.action] || { label: log.action, color: 'bg-gray-100 text-gray-700' };
                  return (
                    <div key={i} className="flex items-start gap-3 text-sm">
                      <span className={`badge ${meta.color} flex-shrink-0 mt-0.5`}>{meta.label}</span>
                      <div className="flex-1 min-w-0">
                        {log.details && (
                          <p className="text-navy-600 text-xs font-mono bg-navy-50 rounded-lg px-2 py-1 mt-0.5 truncate">
                            {JSON.stringify(log.details).slice(0, 80)}…
                          </p>
                        )}
                      </div>
                      <span className="text-navy-400 text-xs flex-shrink-0 flex items-center gap-1">
                        <Clock size={11} />
                        {format(new Date(log.created_at), 'dd MMM yyyy HH:mm', { locale: tr })}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Sağ — meta */}
        <div className="space-y-5">
          {/* Kurum kartı */}
          {firma.kurum && (
            <div className="card p-5">
              <h2 className="text-sm font-semibold text-navy-700 uppercase tracking-wider mb-3">Kurum</h2>
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-semibold text-sm"
                  style={{ backgroundColor: firma.kurum.renk || '#1E3A5F' }}
                >
                  {firma.kurum.ad[0]}
                </div>
                <div>
                  <p className="font-semibold text-navy-800 text-sm">{firma.kurum.ad}</p>
                  <p className="text-navy-400 text-xs">{firma.kurum.firma_sayisi} firma</p>
                </div>
              </div>
            </div>
          )}

          {/* Tarihler */}
          <div className="card p-5">
            <h2 className="text-sm font-semibold text-navy-700 uppercase tracking-wider mb-3">Tarihler</h2>
            <div className="space-y-2">
              <div>
                <p className="text-xs text-navy-400">Eklenme</p>
                <p className="text-sm text-navy-700 font-medium">
                  {format(new Date(firma.created_at), 'dd MMMM yyyy', { locale: tr })}
                </p>
              </div>
              <div>
                <p className="text-xs text-navy-400">Son güncelleme</p>
                <p className="text-sm text-navy-700 font-medium">
                  {format(new Date(firma.updated_at), 'dd MMMM yyyy HH:mm', { locale: tr })}
                </p>
              </div>
            </div>
          </div>

          {/* İletişim durumu */}
          <div className="card p-5">
            <h2 className="text-sm font-semibold text-navy-700 uppercase tracking-wider mb-3">İletişim Durumu</h2>
            <div className="space-y-2">
              {[
                { label: 'Mail', ok: !!firma.mail },
                { label: 'Telefon', ok: !!firma.telefon },
                { label: 'Notlar', ok: !!firma.notlar },
              ].map(({ label, ok }) => (
                <div key={label} className="flex items-center justify-between">
                  <span className="text-sm text-navy-600">{label}</span>
                  <span className={`badge ${ok ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-600'}`}>
                    {ok ? '✓ Var' : '✗ Eksik'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {editing && (
        <FirmaModal
          firma={firma}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); }}
        />
      )}
    </div>
  );
}
